/**
 * Hook that runs token verification for all mints on app startup.
 *
 * Implements CASH-11: on every app launch, verify all active/pending tokens
 * against their mints and zero any spent tokens. Non-blocking — runs in
 * the background without preventing app rendering or navigation.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module cashu/hooks/useStartupTokenSweep
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { VerificationResult } from './useTokenVerification';

/** Wallet factory for creating verification wallets per mint URL. */
export interface SweepWalletFactory {
  /** Create a wallet with checkProofsStates for a given mint URL. */
  readonly createWallet: (mintUrl: string) => {
    checkProofsStates: (
      proofs: Array<{ amount: number; secret: string; C: string; id: string }>,
    ) => Promise<Array<{ Y: string; state: string }>>;
  } | null;
}

/** Minimal mint record for the sweep. */
export interface SweepMintRecord {
  readonly id: string;
  readonly url: string;
}

/** Minimal token record for the sweep. */
export interface SweepTokenRecord {
  readonly mintId: string;
  readonly amount: number;
  readonly secret: string;
  readonly c: string;
  readonly keysetId: string;
  readonly state: string;
  readonly lockedTo: string | null;
  readonly spentAt: string | null;
  readonly spentInTxId: string | null;
}

/** Data source for startup token sweep (testability). */
export interface StartupSweepDataSource {
  /** Active mints to sweep. */
  readonly mints: readonly SweepMintRecord[];
  /** All tokens to verify. */
  readonly tokens: readonly SweepTokenRecord[];
}

/** Per-mint sweep result. */
export interface MintSweepResult {
  readonly mintId: string;
  readonly result: VerificationResult | null;
}

/** Return type for the useStartupTokenSweep hook. */
export interface UseStartupTokenSweepResult {
  /** Whether the sweep is currently running. */
  readonly isRunning: boolean;
  /** Results per mint, populated as sweep completes. */
  readonly results: readonly MintSweepResult[];
}

/**
 * Count proof states from a checkProofsStates response.
 *
 * @param states - Array of proof state results from mint
 * @returns Counts of valid, spent, and pending proofs
 */
function countProofStates(states: Array<{ Y: string; state: string }>): {
  valid: number;
  spent: number;
  pending: number;
} {
  let valid = 0;
  let spent = 0;
  let pending = 0;

  for (const state of states) {
    if (state.state === 'SPENT') spent += 1;
    else if (state.state === 'PENDING') pending += 1;
    else valid += 1;
  }

  return { valid, spent, pending };
}

/**
 * Sweep a single mint's tokens for verification.
 *
 * @param mint - The mint to sweep
 * @param tokens - Verifiable tokens for this mint
 * @param walletFactory - Factory for creating wallet instances
 * @returns Sweep result for this mint
 */
async function sweepMint(
  mint: SweepMintRecord,
  tokens: readonly SweepTokenRecord[],
  walletFactory: SweepWalletFactory,
): Promise<MintSweepResult> {
  const wallet = walletFactory.createWallet(mint.url);
  if (!wallet) {
    return { mintId: mint.id, result: null };
  }

  try {
    const proofs = tokens.map((t) => ({
      amount: t.amount,
      secret: t.secret,
      C: t.c,
      id: t.keysetId,
    }));

    const states = await wallet.checkProofsStates(proofs);
    const counts = countProofStates(states);

    return {
      mintId: mint.id,
      result: { ...counts, errors: 0 },
    };
  } catch {
    return { mintId: mint.id, result: null };
  }
}

/**
 * Run token verification for all active mints on app startup.
 *
 * Queries all active mints and their tokens, then verifies each mint's
 * tokens in sequence. Runs once per app session (ref flag prevents re-runs).
 * Non-blocking: does not prevent rendering or navigation.
 *
 * @param dataSource - Data source providing mints and tokens
 * @param walletFactory - Factory for creating verification wallets
 * @returns Object with isRunning flag and per-mint results
 */
export function useStartupTokenSweep(
  dataSource: StartupSweepDataSource,
  walletFactory: SweepWalletFactory,
): UseStartupTokenSweepResult {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<readonly MintSweepResult[]>([]);
  const hasRunRef = useRef(false);

  const runSweep = useCallback(async (): Promise<void> => {
    const { mints, tokens } = dataSource;
    if (mints.length === 0) return;

    setIsRunning(true);
    const sweepResults: MintSweepResult[] = [];

    for (const mint of mints) {
      const mintTokens = tokens.filter(
        (t) => t.mintId === mint.id && (t.state === 'active' || t.state === 'pending'),
      );

      if (mintTokens.length === 0) {
        sweepResults.push({
          mintId: mint.id,
          result: { valid: 0, spent: 0, pending: 0, errors: 0 },
        });
        continue;
      }

      const mintResult = await sweepMint(mint, mintTokens, walletFactory);
      sweepResults.push(mintResult);
    }

    setResults(sweepResults);
    setIsRunning(false);
  }, [dataSource, walletFactory]);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    void runSweep();
  }, [runSweep]);

  return { isRunning, results };
}
